import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Users, GraduationCap, BookOpen, Megaphone, Upload, CheckCircle2, Clock,
  Trash2, RefreshCw, Plus, Search, ShieldCheck, ShieldAlert, Move, BarChart3,
  Building2, Copy, Power, PowerOff, UserPlus, Sparkles, Languages,
} from "lucide-react";
import LanguagesTab from "../components/LanguagesTab";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "teachers", label: "Mentors", icon: GraduationCap },
  { id: "halaqas", label: "Halaqas", icon: BookOpen },
  { id: "students", label: "Students", icon: Users },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "organizations", label: "Organizations", icon: Building2 },
  { id: "ai-test", label: "AI Test", icon: Sparkles },
  { id: "languages", label: "Languages", icon: Languages },
];

/* ============================================================== Overview */
function OverviewTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load overview"));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-muted-foreground" data-testid="overview-loading">Loading…</div>;
  const tiles = [
    { label: "Students", value: data.students, tone: "forest" },
    { label: "Approved teachers", value: data.teachers_approved, tone: "forest" },
    { label: "Pending teachers", value: data.teachers_pending, tone: "terracotta" },
    { label: "Halaqas", value: data.halaqas, tone: "gold" },
    { label: "Submissions awaiting grade", value: data.submissions_pending, tone: "terracotta" },
    { label: "Active announcements", value: data.active_announcements, tone: "gold" },
  ];
  const tones = { forest: "text-forest-700", gold: "text-gold-600", terracotta: "text-terracotta-600" };
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="overview-grid">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-2xl border border-sand-300 bg-white/70 p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t.label}</div>
          <div className={`mt-2 text-4xl font-display font-semibold ${tones[t.tone]}`}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================== Teachers */
function TeachersTab() {
  const [filter, setFilter] = useState("pending");
  const [list, setList] = useState(null);
  const load = () => {
    setList(null);
    api.get(`/admin/teachers?status=${filter}`).then((r) => setList(r.data)).catch(() => toast.error("Couldn't load teachers"));
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/teachers?status=${filter}`);
        if (!cancelled) setList(r.data);
      } catch {
        if (!cancelled) toast.error("Couldn't load teachers");
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  const approve = async (uid) => {
    try {
      await api.post(`/admin/teachers/${uid}/approve`);
      toast.success("Teacher approved");
      load();
    } catch { toast.error("Couldn't approve"); }
  };
  const revoke = async (uid) => {
    if (!window.confirm("Revoke this teacher's approval? They will keep their halaqas but cannot grade until re-approved.")) return;
    try {
      await api.post(`/admin/teachers/${uid}/revoke`);
      toast.success("Approval revoked");
      load();
    } catch { toast.error("Couldn't revoke"); }
  };
  const makeAdmin = async (t) => {
    if (!window.confirm(`Make ${t.name} an admin? They'll get full access to this panel.`)) return;
    try {
      await api.post(`/admin/users/${t.user_id}/role`, { role: "admin" });
      toast.success(`${t.name} is now an admin`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change role"); }
  };

  return (
    <div className="space-y-4" data-testid="teachers-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 p-1 rounded-full bg-sand-200 border border-sand-300" data-testid="teacher-filter">
          {["pending", "approved"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`teacher-filter-${f}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition capitalize ${
                filter === f ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:text-foreground"
              }`}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={load} className="btn-accent text-sm" data-testid="teachers-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground" data-testid="teachers-empty">
          {filter === "pending" ? "No teachers waiting for approval." : "No approved teachers yet."}
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((t) => (
            <li key={t.user_id} className="hifz-card flex items-center gap-4" data-testid={`teacher-row-${t.user_id}`}>
              <div className="h-10 w-10 rounded-full bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold">
                {(t.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-forest-700">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.email} · {t.classes_count} halaqas</div>
              </div>
              {filter === "pending" ? (
                <button onClick={() => approve(t.user_id)} className="btn-primary text-sm" data-testid={`approve-${t.user_id}`}>
                  <ShieldCheck className="h-4 w-4" /> Approve
                </button>
              ) : (
                <>
                  <button onClick={() => makeAdmin(t)} className="btn-ghost text-sm" data-testid={`make-admin-${t.user_id}`}>
                    <ShieldCheck className="h-4 w-4" /> Make admin
                  </button>
                  <button onClick={() => revoke(t.user_id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" data-testid={`revoke-${t.user_id}`}>
                    <ShieldAlert className="h-4 w-4" /> Revoke
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================== Halaqas */
function HalaqasTab() {
  const [list, setList] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [editing, setEditing] = useState(null); // class object
  const empty = { name: "", description: "", schedule: "", teacher_id: "", zoom_url: "", zoom_passcode: "", recurrence: { days_of_week: [], time: "18:00", tz: "Asia/Kuala_Lumpur" } };
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/admin/halaqas").then((r) => setList(r.data)).catch(() => toast.error("Couldn't load halaqas"));
    api.get("/admin/teachers?status=approved").then((r) => setTeachers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openEdit = (cls) => {
    setEditing(cls);
    setForm({
      name: cls.name || "", description: cls.description || "", schedule: cls.schedule || "",
      teacher_id: cls.teacher_id || "", zoom_url: cls.zoom_url || "", zoom_passcode: cls.zoom_passcode || "",
      recurrence: cls.recurrence || { days_of_week: [], time: "18:00", tz: "Asia/Kuala_Lumpur" },
    });
  };
  const openCreate = () => { setEditing({ new: true }); setForm(empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const toggleDay = (d) => {
    setForm((f) => {
      const cur = f.recurrence?.days_of_week || [];
      const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
      return { ...f, recurrence: { ...f.recurrence, days_of_week: next } };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...form,
      teacher_id: form.teacher_id || null,
      zoom_url: form.zoom_url || null,
      zoom_passcode: form.zoom_passcode || null,
      recurrence: form.recurrence?.days_of_week?.length ? form.recurrence : null,
    };
    try {
      if (editing?.new) await api.post("/classes", payload);
      else await api.patch(`/classes/${editing.class_id}`, payload);
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };

  const del = async (cls) => {
    if (!window.confirm(`Delete halaqa "${cls.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/classes/${cls.class_id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-4" data-testid="halaqas-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">Create, edit, and delete halaqas. Zoom URL and passcode are managed here.</div>
        <button onClick={openCreate} className="btn-primary text-sm" data-testid="halaqas-create-btn">
          <Plus className="h-4 w-4" /> New halaqa
        </button>
      </div>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground">No halaqas yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((c) => (
            <li key={c.class_id} className="hifz-card flex items-center gap-4 flex-wrap" data-testid={`halaqa-row-${c.class_id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="font-display font-semibold text-forest-700">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {c.teacher_name || "No teacher"} · {c.student_count} students · {c.pending_submissions} pending grades
                  {c.zoom_url && <> · <span className="text-forest-700">Zoom set</span></>}
                </div>
                {c.schedule && <div className="text-xs text-muted-foreground mt-0.5">{c.schedule}</div>}
              </div>
              <button onClick={() => openEdit(c)} className="btn-accent text-sm" data-testid={`halaqa-edit-${c.class_id}`}>
                Edit
              </button>
              <button onClick={() => del(c)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" data-testid={`halaqa-delete-${c.class_id}`}>
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4" onClick={close} data-testid="halaqa-edit-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl border border-sand-300 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h3 className="font-display text-xl text-forest-700">{editing?.new ? "New halaqa" : `Edit · ${editing.name}`}</h3>
            <input className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Halaqa name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="halaqa-form-name" />
            <textarea className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="halaqa-form-desc" />
            <input className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Human-readable schedule (e.g. Mon, Wed, Fri · 6:00 PM)" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} data-testid="halaqa-form-schedule" />
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Teacher</div>
              <select className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.teacher_id || ""} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })} data-testid="halaqa-form-teacher">
                <option value="">— Assign later —</option>
                {teachers.map((t) => <option key={t.user_id} value={t.user_id}>{`${t.name} (${t.email})`}</option>)}
              </select>
            </div>
            <div className="grid gap-3">
              <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Zoom URL" value={form.zoom_url} onChange={(e) => setForm({ ...form, zoom_url: e.target.value })} data-testid="halaqa-form-zoom" />
              <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Zoom passcode" value={form.zoom_passcode} onChange={(e) => setForm({ ...form, zoom_passcode: e.target.value })} data-testid="halaqa-form-passcode" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Recurrence — days of week</div>
              <div className="flex flex-wrap gap-1.5" data-testid="halaqa-form-days">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <button key={d} type="button" onClick={() => toggleDay(d)}
                    className={`px-3 py-1.5 rounded-full text-xs border ${form.recurrence?.days_of_week?.includes(d) ? "bg-forest-600 text-sand-100 border-forest-600" : "bg-white border-sand-300 text-foreground/70"}`}>
                    {d}
                  </button>
                ))}
              </div>
              <div className="mt-2 grid gap-3">
                <input type="time" className="rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.recurrence?.time || "18:00"} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, time: e.target.value } })} data-testid="halaqa-form-time" />
                <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Timezone (e.g. Asia/Kuala_Lumpur)" value={form.recurrence?.tz || ""} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, tz: e.target.value } })} data-testid="halaqa-form-tz" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={close} className="px-4 py-2 rounded-xl border border-sand-300 text-sm">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="halaqa-form-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================== Students */
function StudentsTab() {
  const [students, setStudents] = useState(null);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [importClass, setImportClass] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState(null);
  const [progressForm, setProgressForm] = useState({ surah: "", ayah: "", status: "memorized" });

  const load = async () => {
    setStudents(null);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/users?role=student"),
        api.get("/admin/halaqas"),
      ]);
      setStudents(s.data); setClasses(c.data);
    } catch { toast.error("Couldn't load"); }
  };
  useEffect(() => { load(); }, []);

  // Effect-style fetch when active changes
  const activeId = active?.user_id;
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    Promise.all(classes.map((c) =>
      api.get(`/classes/${c.class_id}/students`).then((r) =>
        r.data.some((x) => x.user_id === activeId) ? c : null
      ).catch(() => null)
    )).then((results) => {
      if (!cancelled) setStudentEnrollments({ uid: activeId, list: results.filter(Boolean) });
    });
    return () => { cancelled = true; };
  }, [activeId, classes]);

  const refreshEnrollments = async () => {
    if (!active) return;
    const enrolled = [];
    for (const c of classes) {
      const r = await api.get(`/classes/${c.class_id}/students`).catch(() => ({ data: [] }));
      if (r.data.some((s) => s.user_id === active.user_id)) enrolled.push(c);
    }
    setStudentEnrollments({ uid: active.user_id, list: enrolled });
  };

  const filtered = (students || []).filter((s) => {
    const q = search.trim().toLowerCase();
    return !q || (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
  });

  const enroll = async (class_id) => {
    if (!active) return;
    try {
      await api.post("/admin/enrollments", { class_id, user_id: active.user_id });
      toast.success("Assigned");
      await refreshEnrollments();
    } catch { toast.error("Couldn't assign"); }
  };
  const unenroll = async (class_id) => {
    if (!active) return;
    try {
      await api.delete("/admin/enrollments", { data: { class_id, user_id: active.user_id } });
      toast.success("Removed");
      await refreshEnrollments();
    } catch { toast.error("Couldn't remove"); }
  };

  const submitImport = async (e) => {
    e.preventDefault();
    const file = e.target.elements.csv.files[0];
    if (!file) return toast.error("Pick a CSV file");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (importClass) fd.append("class_id", importClass);
      const r = await fetch(`${API}/admin/students/import`, {
        method: "POST", body: fd,
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Failed");
      setImportResult(data);
      toast.success(`Imported · ${data.created} created, ${data.skipped_existing} existing, ${data.enrolled} enrolled`);
      load();
    } catch (e) { toast.error(e?.message || "Import failed"); }
    finally { setImporting(false); }
  };

  const overrideProgress = async () => {
    if (!active) return;
    if (!progressForm.surah || !progressForm.ayah) return toast.error("Surah & Ayah required");
    try {
      await api.post(`/admin/students/${active.user_id}/progress`, {
        surah: Number(progressForm.surah),
        ayah: Number(progressForm.ayah),
        status: progressForm.status,
      });
      toast.success("Progress set");
    } catch { toast.error("Couldn't set"); }
  };
  const resetProgress = async () => {
    if (!active) return;
    if (!window.confirm(`Reset ALL hifz progress for ${active.name}? This wipes their streak and XP too.`)) return;
    try {
      await api.post(`/admin/students/${active.user_id}/progress`, { reset_all: true });
      toast.success("Progress reset");
      load();
    } catch { toast.error("Couldn't reset"); }
  };

  const changeRole = async (role) => {
    if (!active) return;
    const label = { mentor: "a mentor", teacher: "a school teacher", admin: "an admin", principal: "a principal", translator: "a translator" }[role] || role;
    let assigned_locales;
    if (role === "translator") {
      // A translator works only on the languages assigned to them (same rule as the other apps).
      let hint = "";
      try { hint = (await api.get("/i18n/languages/all")).data.map((l) => l.code).join(", "); } catch { /* the prompt still works */ }
      const raw = window.prompt(`Which languages should ${active.name} translate? Enter language codes separated by commas${hint ? ` (${hint})` : ""}.`, (active.assigned_locales || []).join(", "));
      if (raw === null) return;
      assigned_locales = raw.split(",").map((c) => c.trim().toLowerCase()).filter(Boolean);
      if (assigned_locales.length === 0) return toast.error("Pick at least one language");
    }
    if (!window.confirm(`Make ${active.name} ${label}? They'll see the change next time they load the app.`)) return;
    try {
      const r = await api.post(`/admin/users/${active.user_id}/role`, { role, assigned_locales });
      toast.success(`${active.name} is now ${label}`);
      setActive(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change role"); }
  };

  return (
    <div className="space-y-6" data-testid="students-tab">
      {/* Bulk import */}
      <details className="hifz-card" data-testid="bulk-import">
        <summary className="cursor-pointer font-display font-semibold text-forest-700 flex items-center gap-2">
          <Upload className="h-4 w-4" /> Bulk import students (CSV)
        </summary>
        <form onSubmit={submitImport} className="mt-4 space-y-3 text-sm">
          <p className="text-muted-foreground text-xs">CSV columns: <code>name, email, password</code> (password optional → defaults to <code>Welcome@2026</code>). Pick a halaqa to auto-enroll everyone.</p>
          <input type="file" name="csv" accept=".csv" required className="block text-sm" data-testid="bulk-import-file" />
          <select value={importClass} onChange={(e) => setImportClass(e.target.value)} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="bulk-import-class">
            <option value="">— Don&apos;t auto-enroll —</option>
            {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
          </select>
          <div>
            <button type="submit" disabled={importing} className="btn-primary text-sm" data-testid="bulk-import-submit">
              {importing ? "Importing…" : "Import CSV"}
            </button>
          </div>
          {importResult && (
            <div className="rounded-xl bg-forest-600/8 border border-forest-600/30 p-3 text-xs" data-testid="bulk-import-result">
              ✅ Created {importResult.created} · Skipped {importResult.skipped_existing} · Enrolled {importResult.enrolled}
              {importResult.errors?.length > 0 && <div className="mt-1 text-terracotta-600">{importResult.errors.length} row errors</div>}
            </div>
          )}
        </form>
      </details>

      <div className="grid gap-5">
        <aside className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input className="w-full rounded-xl border border-sand-300 bg-white pl-9 pr-3 py-2 text-sm" placeholder="Search students by name or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="students-search" />
          </div>
          {students === null ? <div className="text-muted-foreground text-sm">Loading…</div> : (
            <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {filtered.map((s) => (
                <li key={s.user_id}>
                  <button onClick={() => setActive(s)} data-testid={`student-row-${s.user_id}`}
                    className={`w-full text-left p-3 rounded-xl border ${active?.user_id === s.user_id ? "border-forest-600 bg-forest-600/8" : "border-sand-300 bg-white hover:bg-sand-50"}`}>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.email} · {s.total_memorized} ayahs · {s.streak}d</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section>
          {!active ? (
            <div className="hifz-card text-center py-12 text-muted-foreground" data-testid="students-pick">
              Pick a student to manage their halaqa assignments and progress.
            </div>
          ) : (
            <div className="hifz-card space-y-5" data-testid="student-detail">
              <header>
                <div className="font-display font-semibold text-xl text-forest-700">{active.name}</div>
                <div className="text-xs text-muted-foreground">{active.email}</div>
              </header>

              {/* Role */}
              <div className="rounded-2xl border border-sand-300 bg-sand-200/40 p-4 space-y-2" data-testid="role-change">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                  <ShieldCheck className="h-3 w-3" /> Role &middot; currently <span className="capitalize text-forest-700 font-bold">{active.role}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => changeRole("student")} disabled={active.role === "student"}
                    className="btn-ghost text-xs disabled:opacity-40" data-testid="role-set-student">Make student</button>
                  <button onClick={() => changeRole("mentor")} disabled={active.role === "mentor"}
                    className="btn-ghost text-xs disabled:opacity-40" data-testid="role-set-mentor">Make mentor (approved)</button>
                  <button onClick={() => changeRole("principal")} disabled={active.role === "principal"}
                    className="btn-ghost text-xs disabled:opacity-40" data-testid="role-set-principal">Make principal</button>
                  <button onClick={() => changeRole("translator")}
                    className="btn-ghost text-xs" data-testid="role-set-translator">
                    {active.role === "translator" ? "Change languages" : "Make translator"}
                  </button>
                  <button onClick={() => changeRole("admin")} disabled={active.role === "admin"}
                    className="btn-ghost text-xs disabled:opacity-40" data-testid="role-set-admin">Make admin</button>
                </div>
                {active.role === "translator" && (active.assigned_locales || []).length > 0 && (
                  <p className="text-[11px] text-muted-foreground" data-testid="role-translator-locales">
                    Translates: <b>{active.assigned_locales.join(", ")}</b>
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  "Make mentor" grants access immediately — no application needed. To review a
                  self-submitted application instead, use the Mentors tab. A principal uses the app like a student;
                  their school-wide view is on the platform dashboard.
                </p>
              </div>

              {/* Enrollments */}
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">Halaqa assignments</div>
                {!studentEnrollments || studentEnrollments.uid !== active.user_id ? <div className="text-sm text-muted-foreground">Loading enrollments…</div> : (
                  <ul className="space-y-1.5 mb-3">
                    {studentEnrollments.list.length === 0 && <li className="text-sm text-muted-foreground">Not in any halaqa.</li>}
                    {studentEnrollments.list.map((c) => (
                      <li key={c.class_id} className="flex items-center justify-between bg-sand-200/40 border border-sand-300 rounded-xl px-3 py-2" data-testid={`enroll-row-${c.class_id}`}>
                        <span className="text-sm">{c.name}</span>
                        <button onClick={() => unenroll(c.class_id)} className="text-xs text-terracotta-600 hover:underline" data-testid={`unenroll-${c.class_id}`}>Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
                <select className="rounded-xl border border-sand-300 px-3 py-2 text-sm w-full" onChange={(e) => { if (e.target.value) { enroll(e.target.value); e.target.value = ""; } }} data-testid="assign-class-select">
                  <option value="">+ Assign to halaqa…</option>
                  {classes.filter((c) => !(studentEnrollments?.uid === active.user_id ? studentEnrollments.list : []).find((x) => x.class_id === c.class_id)).map((c) => (
                    <option key={c.class_id} value={c.class_id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Progress override */}
              <div className="rounded-2xl border border-sand-300 bg-sand-200/40 p-4 space-y-3" data-testid="progress-override">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                  <Move className="h-3 w-3" /> Override progress
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Surah" type="number" value={progressForm.surah} onChange={(e) => setProgressForm({ ...progressForm, surah: e.target.value })} data-testid="prog-surah" />
                  <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Ayah" type="number" value={progressForm.ayah} onChange={(e) => setProgressForm({ ...progressForm, ayah: e.target.value })} data-testid="prog-ayah" />
                  <select className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" value={progressForm.status} onChange={(e) => setProgressForm({ ...progressForm, status: e.target.value })} data-testid="prog-status">
                    {["not_started", "learning", "memorized", "revising"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={overrideProgress} className="btn-accent text-xs" data-testid="prog-set">Set ayah</button>
                </div>
                <button onClick={resetProgress} className="text-xs text-terracotta-600 hover:underline" data-testid="prog-reset">Reset ALL progress (wipe streak + XP)</button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ============================================================== Announcements */
function AnnouncementsTab() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", expires_at: "" });
  const load = () => api.get("/admin/announcements").then((r) => setList(r.data)).catch(() => toast.error("Couldn't load"));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title & body required");
    try {
      const payload = { ...form };
      if (!payload.expires_at) delete payload.expires_at;
      else payload.expires_at = new Date(payload.expires_at).toISOString();
      await api.post("/admin/announcements", payload);
      toast.success("Announcement posted");
      setForm({ title: "", body: "", audience: "all", expires_at: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/admin/announcements/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-4" data-testid="announcements-tab">
      <form onSubmit={create} className="hifz-card space-y-3" data-testid="announcement-form">
        <h3 className="font-display font-semibold text-forest-700">New announcement</h3>
        <input className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Title (max 120 chars)" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title" />
      <textarea className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Body (max 600 chars)" maxLength={600} rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ann-body" />
        <div className="grid gap-3">
          <select className="rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="ann-audience">
            <option value="all">Everyone</option>
            <option value="students">Students only</option>
            <option value="teachers">Mentors only</option>
          </select>
          <input type="date" className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="ann-expires" />
        </div>
        <button type="submit" className="btn-primary text-sm" data-testid="ann-submit"><Megaphone className="h-4 w-4" /> Post</button>
      </form>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground">No announcements yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((a) => (
            <li key={a.id} className="hifz-card flex items-start gap-4" data-testid={`ann-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-forest-700">{a.title}</div>
                <p className="text-sm text-foreground/80 mt-1">{a.body}</p>
                <div className="text-[11px] text-muted-foreground mt-1 capitalize">
                  Audience: {a.audience} · {new Date(a.created_at).toLocaleString()}
                  {a.expires_at && <> · Expires {new Date(a.expires_at).toLocaleDateString()}</>}
                </div>
              </div>
              <button onClick={() => del(a.id)} className="text-terracotta-600 p-2 hover:bg-terracotta-500/10 rounded-lg" data-testid={`ann-delete-${a.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================== Organizations */
function OrganizationsTab() {
  const [list, setList] = useState(null);
  const [expanded, setExpanded] = useState(null); // org_id currently drilled into
  const [roster, setRoster] = useState(null);
  const [form, setForm] = useState({ name: "", plan_tier: "starter", seat_limit: 25 });
  const [adminForm, setAdminForm] = useState(null); // org_id currently provisioning an org_admin for

  const load = () => api.get("/admin/orgs").then((r) => setList(r.data)).catch(() => toast.error("Couldn't load organizations"));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    try {
      const r = await api.post("/admin/orgs", { ...form, seat_limit: Number(form.seat_limit) });
      toast.success(`Created "${r.data.name}" — code ${r.data.org_code}`);
      setForm({ name: "", plan_tier: "starter", seat_limit: 25 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create organization"); }
  };

  const toggleActive = async (org) => {
    try {
      await api.post(`/admin/orgs/${org.org_id}/${org.is_active ? "deactivate" : "activate"}`);
      toast.success(org.is_active ? "Deactivated — no new registrations will be accepted" : "Activated");
      load();
    } catch { toast.error("Couldn't update"); }
  };

  const rotateCode = async (org) => {
    try {
      const r = await api.post(`/admin/orgs/${org.org_id}/invite-code/rotate`);
      toast.success(`New code: ${r.data.org_code}`);
      load();
    } catch { toast.error("Couldn't rotate code"); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success("Code copied"); };

  const createOrgAdmin = async (org) => {
    if (!adminForm?.email || !adminForm?.name || !adminForm?.password) return toast.error("Fill in all fields");
    try {
      await api.post(`/admin/orgs/${org.org_id}/org-admin`, adminForm);
      toast.success("Org admin created");
      setAdminForm(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create org admin"); }
  };

  const drillDown = async (org) => {
    if (expanded === org.org_id) { setExpanded(null); setRoster(null); return; }
    setExpanded(org.org_id); setRoster(null);
    try {
      const [students, teachers, halaqas] = await Promise.all([
        api.get(`/admin/orgs/${org.org_id}/students`),
        api.get(`/admin/orgs/${org.org_id}/teachers`),
        api.get(`/admin/orgs/${org.org_id}/halaqas`),
      ]);
      setRoster({ students: students.data, teachers: teachers.data, halaqas: halaqas.data });
    } catch { toast.error("Couldn't load roster"); }
  };

  return (
    <div className="space-y-4" data-testid="organizations-tab">
      <form onSubmit={create} className="hifz-card space-y-3" data-testid="org-create-form">
        <h3 className="font-display font-semibold text-forest-700">Create organization</h3>
        <div className="grid gap-3">
          <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Institution name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-create-name" />
          <select className="rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.plan_tier} onChange={(e) => setForm({ ...form, plan_tier: e.target.value })} data-testid="org-create-tier">
            <option value="starter">Starter</option>
            <option value="growth">Growth</option>
            <option value="institution">Institution</option>
          </select>
          <input type="number" min={1} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Seat limit" value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: e.target.value })} data-testid="org-create-seats" />
        </div>
        <button type="submit" className="btn-primary text-sm" data-testid="org-create-submit"><Plus className="h-4 w-4" /> Create</button>
      </form>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground">No organizations yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((o) => (
            <li key={o.org_id} className="hifz-card space-y-3" data-testid={`org-row-${o.org_id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-semibold text-forest-700 flex items-center gap-2">
                    {o.name}
                    {!o.is_active && <span className="status-pill text-[10px] border border-terracotta-500/40 text-terracotta-600">Inactive</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 capitalize">
                    {o.plan_tier} · {o.seats_used}/{o.seat_limit} seats · billing: {o.billing_status}
                    {o.public_sharing_enabled && <> · <span className="text-forest-700">Public sharing on</span></>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Code: <code className="font-semibold">{o.org_code}</code>
                  </div>
                </div>
                <button onClick={() => copyCode(o.org_code)} className="btn-ghost text-xs" data-testid={`org-copy-${o.org_id}`}><Copy className="h-3.5 w-3.5" /> Copy code</button>
                <button onClick={() => rotateCode(o)} className="btn-ghost text-xs" data-testid={`org-rotate-${o.org_id}`}><RefreshCw className="h-3.5 w-3.5" /> Rotate</button>
                <button onClick={() => setAdminForm({ org_id: o.org_id, email: "", name: "", password: "" })} className="btn-ghost text-xs" data-testid={`org-add-admin-${o.org_id}`}><UserPlus className="h-3.5 w-3.5" /> Add org admin</button>
                <button onClick={() => toggleActive(o)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border ${o.is_active ? "border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" : "border-forest-600/40 text-forest-700 hover:bg-forest-600/10"}`} data-testid={`org-toggle-${o.org_id}`}>
                  {o.is_active ? <><PowerOff className="h-3.5 w-3.5" /> Deactivate</> : <><Power className="h-3.5 w-3.5" /> Activate</>}
                </button>
                <button onClick={() => drillDown(o)} className="btn-accent text-xs" data-testid={`org-drilldown-${o.org_id}`}>
                  {expanded === o.org_id ? "Hide roster" : "View roster"}
                </button>
              </div>

              {adminForm?.org_id === o.org_id && (
                <div className="rounded-xl border border-sand-300 bg-sand-200/40 p-3 space-y-2" data-testid={`org-admin-form-${o.org_id}`}>
                  <div className="grid gap-2">
                    <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Name" value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} />
                    <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} />
                    <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Temporary password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => createOrgAdmin(o)} className="btn-primary text-xs">Create org admin</button>
                    <button onClick={() => setAdminForm(null)} className="px-3 py-1.5 rounded-xl border border-sand-300 text-xs">Cancel</button>
                  </div>
                </div>
              )}

              {expanded === o.org_id && (
                <div className="rounded-xl border border-sand-300 bg-white p-3 text-sm" data-testid={`org-roster-${o.org_id}`}>
                  {!roster ? <div className="text-muted-foreground text-xs">Loading roster…</div> : (
                    <div className="grid gap-4 text-xs">
                      <div>
                        <div className="font-semibold text-forest-700 mb-1">Students ({roster.students.length})</div>
                        {roster.students.map((s) => <div key={s.user_id} className="text-muted-foreground">{s.name} · {s.email}</div>)}
                      </div>
                      <div>
                        <div className="font-semibold text-forest-700 mb-1">Teachers ({roster.teachers.length})</div>
                        {roster.teachers.map((t) => <div key={t.user_id} className="text-muted-foreground">{t.name} · {t.teacher_status}</div>)}
                      </div>
                      <div>
                        <div className="font-semibold text-forest-700 mb-1">Halaqas ({roster.halaqas.length})</div>
                        {roster.halaqas.map((c) => <div key={c.class_id} className="text-muted-foreground">{c.name} · {c.student_count} students</div>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================== AI Test */
const fmtMin = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function AiTestTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const search = (term = q) =>
    api.get("/admin/ai-test/users", { params: { q: term } })
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Couldn't load users"));
  useEffect(() => { search(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const grant = async (u, days) => {
    setBusyId(u.user_id);
    try {
      await api.post("/admin/ai-test/premium", { user_id: u.user_id, days });
      toast.success(days === 0 ? `Unlimited AI Test removed for ${u.name || u.email}` : `Granted ${days} days of unlimited AI Test to ${u.name || u.email}`);
      await search();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="ai-test-tab">
      <p className="text-sm text-foreground/70 max-w-2xl">
        Free users get 5 minutes of AI Test per day (UTC). Search a user to see today&apos;s usage and grant or remove unlimited AI Test.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…"
          className="flex-1 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="ai-test-search" />
        <button type="submit" className="btn-primary text-sm" data-testid="ai-test-search-btn"><Search className="h-4 w-4" /> Search</button>
      </form>
      {!rows ? <div className="text-muted-foreground">Loading…</div> : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">No users found.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((u) => {
            const owner = u.unlimited;
            return (
              <div key={u.user_id} className="rounded-2xl border border-sand-300 bg-white/70 p-4 space-y-3" data-testid={`ai-test-user-${u.user_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-forest-700 truncate">{u.name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email} · {u.role}</div>
                  </div>
                  {owner ? (
                    <span className="status-pill status-memorized text-[11px]">Unlimited{u.ai_premium_until ? ` until ${new Date(u.ai_premium_until).toLocaleDateString()}` : ""}</span>
                  ) : (
                    <span className="text-xs text-foreground/70">This month: {u.used_tests_this_month ?? 0} / {u.limit_tests ?? 10} AI tests</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[30, 90, 365].map((d) => (
                    <button key={d} disabled={busyId === u.user_id} onClick={() => grant(u, d)}
                      className="btn-ghost text-xs" data-testid={`ai-test-grant-${d}-${u.user_id}`}>+{d} days unlimited</button>
                  ))}
                  {u.ai_premium_until && (
                    <button disabled={busyId === u.user_id} onClick={() => grant(u, 0)}
                      className="btn-ghost text-xs text-terracotta-600" data-testid={`ai-test-revoke-${u.user_id}`}>Remove unlimited</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================== Main */
export default function AdminPanel() {
  const { user } = useAuth();
  const translatorOnly = user?.role === "translator";
  const [tab, setTab] = useState(translatorOnly ? "languages" : "overview");
  const tabs = translatorOnly ? TABS.filter((t) => t.id === "languages") : TABS;
  return (
    <div className="space-y-8" data-testid="admin-panel">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Admin panel</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Manage HikmahVerses</h1>
        <p className="mt-2 text-foreground/70 max-w-2xl">
          Approve teachers, assign students to halaqas, manage zoom links, broadcast announcements, and override progress when needed.
        </p>
      </header>

      <div className="flex flex-wrap gap-1.5 border-b border-sand-300 pb-2" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-t-xl text-sm font-medium transition ${
              tab === t.id ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:bg-sand-200"
            }`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === "overview" && <OverviewTab />}
        {tab === "teachers" && <TeachersTab />}
        {tab === "halaqas" && <HalaqasTab />}
        {tab === "students" && <StudentsTab />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "organizations" && <OrganizationsTab />}
        {tab === "ai-test" && <AiTestTab />}
        {tab === "languages" && <LanguagesTab />}
      </div>
    </div>
  );
}
